import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { firestore, withActor } from '../../backend'
import { setIsBookingsLoaded } from './bookingsReducer'
import { fetchSpaces } from './spacesReducer'
import { fetchSources } from './sourcesReducer'
import { fetchStatus } from './statusReducer'
import { fetchUsers } from './usersReducer'
import { fetchProducts } from './productsReducer'
import { fetchCustomDates, setIsCustomDatesLoaded } from './customDatesReducer'
import { fetchSlots } from './slotsReducer'
import { fetchTemplates } from './templatesReducer'
import { fetchRooms, setIsRoomsLoaded } from './roomsReducer'
import { fetchFiles } from './filesReducer'
import { fetchGlobalVariables, fetchVariables } from './variablesReducer'
import { fetchLinks, setIsLinksLoaded } from './linksReducer'
import { fetchSteps } from './stepsReducer'
import { fetchCustomFields } from './customFieldsReducer'
import { collection, query, where, updateDoc, doc, onSnapshot } from 'firebase/firestore'
import i18n from '../../i18n'
import { upsertConnector, removeConnector } from '../../utils/connectors'

// BS-2293 : `connect` ne se réécrit jamais en bloc (cf. utils/connectors.js).
const assertNoConnect = data => {
	if (data && Object.prototype.hasOwnProperty.call(data, 'connect')) {
		throw new Error('BS-2293: use saveVenueConnector/removeVenueConnector (or the user variants) to write `connect`')
	}
}

export const saveVenue = createAsyncThunk('venues/saveVenue', async ({ id, data }) => {
	assertNoConnect(data)
	return updateDoc(doc(firestore, 'etablissements', id), withActor(data))
})

// BS-2293 : ne jamais écrire `connect` via saveVenue depuis une copie en mémoire,
// passer par ces deux thunks (relecture transactionnelle du document).
export const saveVenueConnector = createAsyncThunk('venues/saveVenueConnector', async ({ id, connector, preserve }) => {
	return upsertConnector('etablissements', id, connector, { preserve })
})

export const removeVenueConnector = createAsyncThunk('venues/removeVenueConnector', async ({ id, connectorId }) => {
	return removeConnector('etablissements', id, connectorId)
})

const setLoading = state => {
	state.status = 'loading'
}
const setSucceeded = state => {
	state.status = 'succeeded'
}
const setFailed = (state, action) => {
	state.status = 'failed'
	state.error = action.error.message
}

export const venuesSlice = createSlice({
	name: 'venues',
	initialState: {
		value: [],
		status: 'idle',
		error: null,
		selectedVenue: {},
		venuesGroup: {},
	},
	reducers: {
		setVenues: (state, action) => {
			state.value = action.payload
		},
		setVenueStatus: (state, action) => {
			state.status = action.payload
		},
		setInitialSelectedVenue: (state, action) => {
			if (!state.selectedVenue.id) {
				state.selectedVenue = action.payload
			}
		},
		setSelectedVenue: (state, action) => {
			state.selectedVenue = action.payload
		},
		setVenuesGroup: (state, action) => {
			state.venuesGroup = action.payload
		},
	},
	extraReducers: {
		[saveVenue.pending]: setLoading,
		[saveVenue.fulfilled]: setSucceeded,
		[saveVenue.rejected]: setFailed,
		[saveVenueConnector.pending]: setLoading,
		[saveVenueConnector.fulfilled]: setSucceeded,
		[saveVenueConnector.rejected]: setFailed,
		[removeVenueConnector.pending]: setLoading,
		[removeVenueConnector.fulfilled]: setSucceeded,
		[removeVenueConnector.rejected]: setFailed,
	},
})

export const fetchVenuesGroup = venuesGroupId => dispatch => {
	return onSnapshot(
		doc(firestore, 'venues_groups', venuesGroupId),
		querySnapshot => {
			const result = { ...querySnapshot.data(), id: querySnapshot.id }
			dispatch(setVenuesGroup(result))
		},
		error => {
			console.log(error)
		}
	)
}

export const fetchVenues =
	(userId, handleSetListeners, willLoadAllVenues = false) =>
	(dispatch, getState) => {
		return onSnapshot(
			query(collection(firestore, 'etablissements'), where('users', 'array-contains', userId)),
			querySnapshot => {
				const venues = querySnapshot.docs
					.map(doc => ({ ...doc.data(), id: doc.id }))
					.sort((a, b) => {
						if (a.nom > b.nom) return 1
						if (a.nom < b.nom) return -1
						return 0
					})
				dispatch(setVenues(venues))

				const { selectedVenue } = getState().venues
				const { venuesGroupId } = getState().users.activeUser

				if (selectedVenue?.id) {
					dispatch(setSelectedVenue(venues.find(({ id }) => id === selectedVenue.id)))
				} else {
					const initialSelectedVenue = venues[0]
					i18n.changeLanguage(initialSelectedVenue.defaultLang || 'fr')
					const venueIds = willLoadAllVenues ? venues.map(({ id }) => id) : [initialSelectedVenue.id]

					dispatch(setSelectedVenue(initialSelectedVenue))

					const arr = [
						dispatch(fetchLinks(initialSelectedVenue.id)),
						dispatch(fetchCustomDates(initialSelectedVenue.id)),
						dispatch(fetchRooms(initialSelectedVenue.id)),
					]

					handleSetListeners(arr)
					dispatch(fetchGlobalVariables())
					dispatch(fetchTemplates(initialSelectedVenue.id))
					dispatch(fetchVariables(initialSelectedVenue.id))
					dispatch(fetchFiles(initialSelectedVenue.id))
					dispatch(fetchSlots(initialSelectedVenue.id))
					dispatch(fetchProducts(initialSelectedVenue.id))
					dispatch(fetchCustomFields({ venueIds, venueId: initialSelectedVenue.id, venuesGroupId }))
					dispatch(fetchSources({ venueIds, venueId: initialSelectedVenue.id }))
					dispatch(fetchStatus({ venueIds, venueId: initialSelectedVenue.id }))
					dispatch(fetchSpaces({ venueIds, venueId: initialSelectedVenue.id }))
					dispatch(fetchUsers({ venueIds, venueId: initialSelectedVenue.id }))
					dispatch(fetchSteps(initialSelectedVenue.id))
					// if (venuesGroupId) {
					// 	dispatch(fetchVenuesGroup(venuesGroupId))
					// }
				}
			},
			error => {
				console.log(error)
			}
		)
	}

export const loadVenue = (venueId, handleSetListeners) => (dispatch, getState) => {
	const venue = getState().venues.value.find(({ id }) => id === venueId)
	// const venueIds = [venueId]
	const venueIds = getState().venues.value.map(({ id }) => id)
	const { venuesGroupId } = getState().users.activeUser

	i18n.changeLanguage(venue.defaultLang || 'fr')

	dispatch(setSelectedVenue(venue))
	dispatch(setIsBookingsLoaded(false))
	dispatch(setIsCustomDatesLoaded(false))
	dispatch(setIsLinksLoaded(false))
	dispatch(setIsRoomsLoaded(false))

	const arr = [dispatch(fetchLinks(venueId)), dispatch(fetchCustomDates(venueId)), dispatch(fetchRooms(venueId))]

	handleSetListeners(arr)

	dispatch(fetchGlobalVariables())
	dispatch(fetchTemplates(venueId))
	dispatch(fetchVariables(venueId))
	dispatch(fetchFiles(venueId))
	dispatch(fetchSlots(venueId))
	dispatch(fetchCustomDates(venueId))
	dispatch(fetchProducts(venueId))
	dispatch(fetchCustomFields({ venueIds, venueId, venuesGroupId }))
	dispatch(fetchSources({ venueIds, venueId }))
	dispatch(fetchStatus({ venueIds, venueId }))
	dispatch(fetchSpaces({ venueIds, venueId }))
	dispatch(fetchUsers({ venueIds, venueId }))
	dispatch(fetchSteps(venueId))
}

export const getVenues = state => state.venues.value
export const getVenueStatus = state => state.venues.status
export const getVenueError = state => state.venues.error
export const getSelectedVenue = state => state.venues.selectedVenue
export const getVenuesGroup = state => state.venues.venuesGroup

export const { setVenues, setVenuesGroup, setVenueStatus, setInitialSelectedVenue, setSelectedVenue } = venuesSlice.actions

export default venuesSlice.reducer
